import React from 'react';

interface TextPreviewerProps {
    content: string;
    component?: Frame.Component;
}

const TextPreviewer: React.FC<TextPreviewerProps> = ({ content, component }) => {
    const getTextAlign = () => {
        if (!component?.contentPosition) return 'center';
        switch (component.contentPosition) {
            case 'Left':
                return 'left';
            case 'Right':
                return 'right';
            case 'Center':
            default:
                return 'center';
        }
    };

    const getJustifyContent = () => {
        if (!component?.contentPosition) return 'center';
        switch (component.contentPosition) {
            case 'Left':
                return 'flex-start';
            case 'Right':
                return 'flex-end';
            case 'Center':
            default:
                return 'center';
        }
    };

    const getFontWeight = () => {
        if (!component?.fontWeight) return 'normal';
        
        // Handle numeric values (100, 200, ..., 900)
        const numericWeight = Number(component.fontWeight);
        if (!isNaN(numericWeight)) {
            return numericWeight;
        }
        
        // Handle string values (normal, bold, bolder, lighter)
        const weightMap: Record<string, number | string> = {
            'thin': 100,
            'extra-light': 200,
            'extralight': 200,
            'light': 300,
            'normal': 400,
            'regular': 400,
            'medium': 500,
            'semi-bold': 600,
            'semibold': 600,
            'bold': 700,
            'extra-bold': 800,
            'extrabold': 800,
            'black': 900,
            'heavy': 900,
            'bolder': 'bolder',
            'lighter': 'lighter'
        };
        
        const lowerWeight = String(component.fontWeight).toLowerCase();
        return weightMap[lowerWeight] || component.fontWeight;
    };

    const getFontSize = () => {
        // If fontSize is explicitly set and valid, use it
        if (component?.fontSize && component.fontSize > 0) {
            return `${component.fontSize}px`;
        }
        // Default font size (14px fallback)
        return '14px';
    };

    return (
        <div
            className="w-full h-full flex items-center"
            style={{
                boxSizing: 'border-box',
                overflow: 'hidden',
                padding: '8px',
                justifyContent: getJustifyContent(),
                border: component?.borderThickness 
                    ? `${component.borderThickness}px solid ${component.borderColor || '#000'}`
                    : 'none',
                borderRadius: component?.borderRadius ? `${component.borderRadius}px` : '0',
                backgroundImage: component?.backgroundImage 
                    ? `url(${component.backgroundImage})`
                    : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div
                className="break-words w-full"
                style={{
                    fontSize: component?.fontSize ? `${component.fontSize}px` : 'clamp(8px, 1.5vw, 16px)',
                    fontWeight: getFontWeight(),
                    fontFamily: component?.fontFamily || 'inherit',
                    color: component?.color || '#000',
                    textAlign: getTextAlign(),
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                    display: '-webkit-box',
                    WebkitLineClamp: 10,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}
            >
                {content}
            </div>
        </div>
    );
};

export default TextPreviewer;
